<template>
  <div class="auth-redirect-container">
    <div v-if="isLoading" class="loading-content text-center">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Chargement...</span>
      </div>
      <p class="mt-3">{{ currentStep }}</p>
    </div>
    
    <div v-if="errorMessage" class="error-content text-center">
      <div class="alert alert-danger">
        <h5>{{ $t('auth_redirect.error_title') }}</h5>
        <p>{{ errorMessage }}</p>
        <router-link to="/login-proprietaire" class="btn btn-primary mt-3">
          retour à la page de connexion
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { useAuthStore } from "@/stores/authStore";
import { useRegisterStore } from "@/stores/register";
import { extractUserFromToken, validateUserRole } from "@/utils/jwtHelpers";

export default {
  name: "AuthEnTantQue",
  data() {
    return {
      isLoading: true,
      errorMessage: null,
      currentStep: ""
    };
  },
  mounted() {
    this.handleTokenAuthentication();
  },
  methods: {
    async handleTokenAuthentication() {
      try {
        // Step 1: Get token from URL query parameters
        const token = this.$route.query.token;
        
        if (!token) {
          this.errorMessage = this.$t('auth_redirect.errors.no_token');
          this.isLoading = false;
          return;
        }

        // Step 2: Logout current user if authenticated
        this.currentStep = this.$t('auth_redirect.clearing_session');
        await this.forceLogout();

        // Step 3: Validate the token structure
        this.currentStep = this.$t('auth_redirect.validating_token');
        const jwtUserData = extractUserFromToken(token);
        if (!jwtUserData) {
          this.errorMessage = this.$t('auth_redirect.errors.invalid_token');
          this.isLoading = false;
          return;
        }

        // Step 4: Determine user role and validate permissions
        this.currentStep = this.$t('auth_redirect.checking_permissions');
        const userRole = this.determineUserRole(jwtUserData, token);
        
        if (!userRole) {
          this.errorMessage = this.$t('auth_redirect.errors.access_denied');
          this.isLoading = false;
          return;
        }

        // Step 5: Set up stores and authentication based on role
        this.currentStep = this.$t('auth_redirect.establishing_session');
        await this.establishNewSession(token, jwtUserData, userRole);
        
        // Step 6: Redirect to dashboard
        this.currentStep = this.$t('auth_redirect.redirecting');
        setTimeout(() => {
          this.$router.push("/dashboard");
        }, 1000);

      } catch (error) {
        console.error("Erreur lors de l'authentification par token:", error);
        this.errorMessage = this.$t('auth_redirect.errors.connection_failed');
        this.isLoading = false;
      }
    },

    determineUserRole(jwtUserData, token) {

        
      // Check for each possible role in order of priority
      if (validateUserRole(token, "client")) {
        return "client";
      } else if (validateUserRole(token, "veterinaire")) {
        return "veterinaire";
      } else if (validateUserRole(token, "delegues")) {
        return "delegues";
      } else if (jwtUserData.role) {
        // Fallback to the role from JWT data
        return jwtUserData.role;
      }
      
      return null;
    },

    async forceLogout() {
      try {
        const authStore = useAuthStore();
        const registerStore = useRegisterStore();
        
        // Clear all stores
        authStore.clearAuth();
        registerStore.clearUser();
        localStorage.clear();
        
        // Clear any localStorage/sessionStorage tokens
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('user_data');
        
        // Wait a bit to ensure cleanup is complete
        await new Promise(resolve => setTimeout(resolve, 500));
        
      } catch (error) {
        console.warn("Error during logout cleanup:", error);
        // Continue anyway as we want to force new authentication
      }
    },

    async establishNewSession(token, jwtUserData, userRole) {
      const authStore = useAuthStore();
      const registerStore = useRegisterStore();

      const user = {
        id: jwtUserData.id,
        email: jwtUserData.email,
        fullName: jwtUserData.fullName,
        firstName: jwtUserData.firstName,
        lastName: jwtUserData.lastName,
        gender: jwtUserData.gender,
        role: userRole,
      };

      authStore.setToken(token);
      authStore.setUser(user);
      registerStore.setUser(user);

      // Prepare user data based on role
      const userData = {
        lastName: jwtUserData.lastName,
        firstName: jwtUserData.firstName,
        fullName: jwtUserData.fullName,
        email: jwtUserData.email,
        phone: null,
        postcode: null,
        street: null,
        city: null,
        customer_infos: {
          id: jwtUserData.id,
          email: jwtUserData.email,
          fullName: jwtUserData.fullName,
          firstName: jwtUserData.firstName,
          lastName: jwtUserData.lastName,
          gender: jwtUserData.gender,
          role: userRole,
        },
        address_infos: null,
      };

      // Use appropriate store method based on role
      if (userRole === "client") {
        registerStore.setInfoveterinaire(userData);
      } else if (userRole === "veterinaire") {
        registerStore.setInfoveterinaire(userData);
      } else if (userRole === "delegues") {
        registerStore.setInfoveterinaire(userData);
      }
      
      // Wait for stores to be updated
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }
};
</script>

<style scoped>
.auth-redirect-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fa;
}

.loading-content, .error-content {
  max-width: 500px;
  padding: 2rem;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.d-flex.gap-2 {
  gap: 0.5rem;
}

.btn {
  min-width: 120px;
}
</style>